import { Injectable, Injector } from '@angular/core';
import { Router, RouterStateSnapshot, UrlTree } from '@angular/router';
import { catchError, map, Observable, of } from 'rxjs';
import { AuthService } from '../services/auth.service';
import { RfJwtService } from '../services/rf-jwt.service';
import { safeReturnUrl } from '../utils/return-url.util';

/**
 * Guards every signed-in layout (client, tenant, owner, service) on the one thing
 * they all assume: that a usable access token exists by the time a route
 * activates. `onboarding` is deliberately NOT guarded: it is reached from
 * emailed links by people who may have no session yet.
 *
 * This used to be `try { return true; }` -- it let everything through, and the
 * `catch` it carried could never fire. The assumption it was meant to enforce is
 * real and widespread: around twenty-five routed components initialise a field
 * with `claims = RfJwtService.getCustomClaims()`, and that throws a bare
 * `'no jwt'` string when storage is empty. PageSidebarService does the same
 * inside its constructor effect. So a page load whose access token had expired
 * -- the state `validateExpiration()` leaves behind, and the normal state after
 * fifteen minutes idle -- could not construct the component, the navigation
 * died, and the user was left on a blank page at `/` with `ERROR no jwt` in the
 * console and no way forward. The session was usually still perfectly good; only
 * the fifteen-minute access token had lapsed (RF-3066).
 *
 * The fix belongs here rather than at each of those call sites: a guard runs
 * before anything is constructed, so by the time any of them reads the claims
 * there is a token to read. It predates RF-3055 -- `/auth/login` grew its own
 * recovery in `refreshThenResume()` while the deep-link path never had one, so
 * "go to the login page" was the only way back in.
 *
 * Three outcomes:
 *  - a live access token: activate, no request;
 *  - expired or missing, but a refresh token is held: refresh, then activate --
 *    the user never sees it happen;
 *  - no refresh token, or the refresh is refused: the login form, carrying the
 *    requested URL so they land where they were going.
 *
 * `AuthService.refresh()` is called directly rather than through a copy of
 * `refreshThenResume()`: that method is the login page's own wrapper, which
 * resumes to a *portal* chosen from the claims, while a guard must resume to the
 * URL that was asked for. `refresh()` is the part worth sharing, and it already
 * collapses concurrent callers into one request, so several guarded routes
 * resolving together still make a single `/refresh` call.
 */
@Injectable({
    providedIn: 'root',
})
export class LoginGuard {
    /**
     * `Injector` rather than `AuthService` directly, and resolved only when a
     * refresh is actually needed.
     *
     * A guard is constructed as part of the navigation, so anything in its
     * constructor runs before its body does -- and `AuthService` drags in a
     * graph of root services, several of which read
     * `RfJwtService.getCustomClaims()` and so throw `'no jwt'` when there is no
     * token. That throw would happen while the guard was being built, which is
     * exactly the situation this guard exists to handle: the navigation would
     * die before the body could redirect, and Angular resets the URL to `/`,
     * leaving the blank page. Depending on nothing that reads claims keeps the
     * guard constructible in the one state it has to work in.
     */
    constructor(
        private injector: Injector,
        private router: Router,
    ) {}

    canActivateChild(
        _route: unknown,
        state: RouterStateSnapshot,
    ): Observable<boolean | UrlTree> | boolean | UrlTree {
        // validateExpiration() clears a token it finds expired, which is what we
        // want: everything below then agrees there is no usable access token.
        if (RfJwtService.raw() && RfJwtService.validateExpiration()) {
            return true;
        }

        if (!RfJwtService.getRefresh()) {
            return this.loginPageFor(state.url);
        }

        return this.injector
            .get(AuthService)
            .refresh()
            .pipe(
                // The interceptor stores the new access token off the /refresh
                // response, so success is judged by what actually landed in storage
                // rather than by the call not throwing.
                map(() =>
                    RfJwtService.raw() ? true : this.loginPageFor(state.url),
                ),
                // refresh() already tears the session down and routes to the login
                // page on failure. Returning a UrlTree as well is harmless and keeps
                // this guard's own contract honest: it never resolves `true` without
                // a token.
                catchError(() => of(this.loginPageFor(state.url))),
            );
    }

    /**
     * The login page, carrying where the user was trying to go.
     *
     * Sanitised here as well as where it is consumed. `state.url` is this app's
     * own router state so it is trustworthy, but the guard is not the only way a
     * `uri` reaches the login page and the check is cheap.
     */
    private loginPageFor(requestedUrl: string): UrlTree {
        const uri = safeReturnUrl(requestedUrl);

        // The raw path, NOT encodeURIComponent'd: Angular's UrlSerializer encodes
        // query values itself, so pre-encoding produced `%252F` on the wire -- a
        // double encode that only survived because the consumer happened to decode
        // twice as well. Passing it raw keeps one encode and one decode.
        return this.router.createUrlTree(
            ['/auth/login'],
            uri ? { queryParams: { uri } } : {},
        );
    }
}
